import { useEffect, useRef, useState } from "react";

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function loadGoogleScript() {
    return new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
        if (existingScript) {
            if (window.google?.accounts?.id) {
                resolve();
                return;
            }

            existingScript.addEventListener("load", resolve, { once: true });
            existingScript.addEventListener("error", reject, { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = GOOGLE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Google sign-in"));
        document.head.appendChild(script);
    });
}

export default function GoogleSignInButton({ mode = "signin", onCredential, onError }) {
    const containerRef = useRef(null);
    const [scriptReady, setScriptReady] = useState(false);
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    useEffect(() => {
        let cancelled = false;

        loadGoogleScript()
            .then(() => {
                if (!cancelled) setScriptReady(true);
            })
            .catch((error) => {
                if (!cancelled) onError?.(error);
            });

        return () => {
            cancelled = true;
        };
    }, [onError]);

    useEffect(() => {
        if (!clientId || !scriptReady || !containerRef.current || !window.google?.accounts?.id) {
            return;
        }

        containerRef.current.innerHTML = "";
        window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (response) => {
                if (response?.credential) {
                    onCredential?.(response.credential);
                } else {
                    onError?.(new Error("Google sign-in did not return a credential"));
                }
            },
        });

        window.google.accounts.id.renderButton(containerRef.current, {
            theme: "outline",
            size: "large",
            shape: "pill",
            text: mode === "signup" ? "signup_with" : "signin_with",
            width: 320,
        });
    }, [clientId, mode, onCredential, onError, scriptReady]);

    if (!clientId) {
        return (
            <div style={{ fontSize: 12, color: "#7a5550", textAlign: "center" }}>
                Google sign-in is unavailable until `REACT_APP_GOOGLE_CLIENT_ID` is configured.
            </div>
        );
    }

    return <div ref={containerRef} style={{ display: "flex", justifyContent: "center" }} />;
}
